import React, { useState } from 'react'
import { Card, Form, Input, Button, Divider, Space, Tag, Typography, message } from 'antd'
import { LockOutlined, CheckCircleOutlined } from '@ant-design/icons'
import { useAuth } from '../../../auth/hooks/useAuth'
import { authService } from '../../../shared/api/services/auth.service'

const { Text } = Typography

export const SecuritySettings: React.FC = () => {
    const { user } = useAuth()
    const [form] = Form.useForm()
    const [loading, setLoading] = useState(false)

    const handleChangePassword = async (values: any) => {
        try {
            setLoading(true)
            await authService.changePassword({
                currentPassword: values.currentPassword,
                newPassword: values.newPassword,
            })
            message.success('Password changed successfully')
            form.resetFields()
        } catch (error: any) {
            const apiError = error?.response?.data?.error
            const errorMessage = apiError?.message || error?.response?.data?.message || 'Failed to change password'

            // Handle field-level validation errors
            if (apiError?.errors) {
                const fieldErrors = Object.entries(apiError.errors).map(([field, messages]: [string, any]) => ({
                    name: field,
                    errors: Array.isArray(messages) ? messages : [messages],
                }))
                form.setFields(fieldErrors)
            }

            message.error(errorMessage)
        } finally {
            setLoading(false)
        }
    }


    return (
        <Card title="Security Settings">
            <Form form={form} layout="vertical" onFinish={handleChangePassword}>
                <Form.Item
                    label="Current Password"
                    name="currentPassword"
                    rules={[{ required: true, message: 'Please enter current password' }]}
                >
                    <Input.Password prefix={<LockOutlined />} placeholder="Enter current password" />
                </Form.Item>

                <Form.Item
                    label="New Password"
                    name="newPassword"
                    rules={[
                        { required: true, message: 'Please enter new password' },
                        { min: 8, message: 'Password must be at least 8 characters' }
                    ]}
                >
                    <Input.Password prefix={<LockOutlined />} placeholder="Enter new password" />
                </Form.Item>

                <Form.Item
                    label="Confirm New Password"
                    name="confirmPassword"
                    dependencies={['newPassword']}
                    rules={[
                        { required: true, message: 'Please confirm your password' },
                        ({ getFieldValue }) => ({
                            validator(_, value) {
                                if (!value || getFieldValue('newPassword') === value) {
                                    return Promise.resolve()
                                }
                                return Promise.reject(new Error('Passwords do not match'))
                            },
                        }),
                    ]}
                >
                    <Input.Password prefix={<LockOutlined />} placeholder="Confirm new password" />
                </Form.Item>

                <Form.Item>
                    <Button type="primary" htmlType="submit" loading={loading}>
                        Change Password
                    </Button>
                </Form.Item>
            </Form>


            <Divider />

            <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                        <Text strong>Two-Factor Authentication</Text>
                        <br />
                        <Text type="secondary">Add an extra layer of security to your account</Text>
                    </div>
                    <Tag icon={<CheckCircleOutlined />} color="success">
                        Enabled
                    </Tag>
                </div>

                {/* TODO: Wire to notification preferences API when available */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', opacity: 0.5 }}>
                    <div>
                        <Text strong>Email Notifications</Text>
                        <br />
                        <Text type="secondary">Receive email alerts for important activities</Text>
                    </div>
                    <Tag color="default">Coming Soon</Tag>
                </div>

                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', opacity: 0.5 }}>
                    <div>
                        <Text strong>Login Alerts</Text>
                        <br />
                        <Text type="secondary">Get notified when someone logs into your account</Text>
                    </div>
                    <Tag color="default">Coming Soon</Tag>
                </div>
            </Space>
        </Card>
    )
}
